import { useSnapshot } from 'valtio';

import Text from '@cartwhl/ui/base/TextNew';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

import { userStore } from 'stores/global';

import {
  isDisplayableAssetCreator,
  resolveAssetCreatorDisplayName,
} from '../assetLibraryFilters';
import { formatAssetCreatedLabel } from '../assetLibraryMediaPreview';
import type { AssetLibraryMediaPreviewState } from '../assetLibraryMediaPreview';
import type { AssetLibraryMediaSelection } from '../assetLibraryMediaSelection';
import { resolveDeletablePreviewAsset } from '../assetLibraryAssetLifecycle';
import {
  buildAssetLibraryShareUrl,
  copyAssetLibraryShareLink,
} from '../assetLibraryShareLink';
import { useStore } from '../assetLibraryStore';
import type { AssetLibraryDisplayItem } from '../utils';
import { useAssetLibraryAssetActions } from '../hooks/useAssetLibraryAssetActions';
import AssetLibraryMediaPreviewVideo from './AssetLibraryMediaPreviewVideo';
import AssetLibraryMediaPreviewOpenWithButton from './AssetLibraryMediaPreviewOpenWithButton';
import AssetLibraryCreatorAvatar from './AssetLibraryCreatorAvatar';
import {
  AssetLibraryPreviewBookmarkButton,
  AssetLibraryPreviewMoveToTrashButton,
  AssetLibraryPreviewShareLinkButton,
} from './AssetLibraryPreviewModalActions';

/** White + hairline border; matches batch preview modal controls. */
const OUTLINE_WHITE_BUTTON =
  '!bg-white dark:!bg-gray-90 !border-gray-30 dark:!border-gray-75 border hover:!bg-gray-15 dark:hover:!bg-gray-75';

export type AssetLibraryMediaPreviewModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  mediaUrl: string | null;
  mediaType: 'image' | 'video';
  createdAt: string | null;
  createdBy: string | null;
  isBookmarked: boolean;
  showMainLibraryPreviewActions: boolean;
  mediaSelection: AssetLibraryMediaSelection | null;
  showOpenWithActions: boolean;
  onBookmark?: () => void;
  onCopyShareLink?: () => void;
  onMoveToTrash?: () => void;
  moveToTrashDisabled?: boolean;
};

export default function AssetLibraryMediaPreviewModal({
  open,
  onOpenChange,
  title,
  mediaUrl,
  mediaType,
  createdAt,
  createdBy,
  isBookmarked,
  showMainLibraryPreviewActions,
  mediaSelection,
  showOpenWithActions,
  onBookmark,
  onCopyShareLink,
  onMoveToTrash,
  moveToTrashDisabled = false,
}: AssetLibraryMediaPreviewModalProps) {
  const { organizationMembers } = useSnapshot(userStore.state);
  const authorName = resolveAssetCreatorDisplayName(
    createdBy,
    organizationMembers,
  );
  const showAuthorAvatar =
    Boolean(createdBy) && isDisplayableAssetCreator(createdBy ?? undefined);
  const showAuthor = Boolean(createdBy || createdAt);

  const modalFooter = (
    <div className="flex flex-wrap items-center justify-between gap-3 gap-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className={OUTLINE_WHITE_BUTTON}
          onClick={() => onOpenChange(false)}
        >
          Close
        </Button>
        {showMainLibraryPreviewActions ? (
          <AssetLibraryPreviewMoveToTrashButton
            disabled={moveToTrashDisabled}
            onClick={onMoveToTrash}
          />
        ) : null}
      </div>
      {showOpenWithActions && mediaSelection ? (
        <AssetLibraryMediaPreviewOpenWithButton
          mediaSelection={mediaSelection}
          onNavigate={() => onOpenChange(false)}
        />
      ) : null}
    </div>
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={
        <div className="flex w-full min-w-0 items-center justify-between gap-3">
          <Text
            token="display/14/medium"
            leading="headline"
            className="text-primary mt-2 mb-2 min-w-0 flex-1 truncate text-pretty"
            title={title}
          >
            {title}
          </Text>
          {showMainLibraryPreviewActions ? (
            <div className="flex shrink-0 items-center gap-2">
              <AssetLibraryPreviewShareLinkButton onClick={onCopyShareLink} />
              <AssetLibraryPreviewBookmarkButton
                isBookmarked={isBookmarked}
                onClick={onBookmark}
              />
            </div>
          ) : null}
        </div>
      }
      maxWidth={640}
      fixedHeightContent={false}
      fullscreenOnMobile
      horizontalBleed
      verticalBleed
      titleClassName="!mb-2 !px-4 !pt-4 lg:!mb-3 lg:!px-5 lg:!pt-5"
      footer={modalFooter}
      footerClassName="overflow-visible px-4 pb-4 pt-3 lg:px-5 lg:pb-5 lg:pt-4"
    >
      <div className="flex flex-col gap-3 px-4 pb-4 lg:gap-4 lg:px-5 lg:pb-5">
        <div className="bg-tertiary relative mx-auto aspect-video w-full max-w-lg shrink-0 overflow-hidden rounded-xl">
          {mediaUrl ? (
            mediaType === 'video' ? (
              <AssetLibraryMediaPreviewVideo src={mediaUrl} />
            ) : (
              <img
                src={mediaUrl}
                alt=""
                crossOrigin="anonymous"
                className="h-full w-full object-contain"
              />
            )
          ) : null}
        </div>

        {showAuthor ? (
          <div className="flex min-w-0 flex-col gap-3">
            {createdBy && (authorName || showAuthorAvatar) ? (
              <div className="flex min-w-0 items-center gap-2">
                {showAuthorAvatar ? (
                  <AssetLibraryCreatorAvatar
                    userID={createdBy}
                    organizationMembers={organizationMembers}
                    size={5}
                  />
                ) : null}
                <Text token="14/medium" className="text-primary truncate">
                  {authorName ?? 'Unknown author'}
                </Text>
              </div>
            ) : null}
            {createdAt ? (
              <Text token="14" className="text-tertiary">
                {formatAssetCreatedLabel(createdAt)}
              </Text>
            ) : null}
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function buildDisplayItemFromMediaPreview(
  mediaPreview: Pick<
    AssetLibraryMediaPreviewState,
    | 'mediaSelection'
    | 'title'
    | 'createdAt'
    | 'createdBy'
    | 'isBookmarked'
  >,
): AssetLibraryDisplayItem | null {
  const selection = mediaPreview.mediaSelection;
  if (!selection) {
    return null;
  }

  const createdAt = mediaPreview.createdAt ?? new Date(0).toISOString();

  return {
    assetID: selection.assetID,
    assetName: mediaPreview.title || selection.label,
    assetType:
      selection.kind === 'media-video' ? 'MEDIA_VIDEO' : 'MEDIA_IMAGE',
    createdAt,
    createdBy: mediaPreview.createdBy ?? '',
    createdByUser: mediaPreview.createdBy ?? '',
    lastUpdatedAt: createdAt,
    thumbnailURL: selection.thumbnailURL,
    isBookmarked: mediaPreview.isBookmarked,
  };
}

export function AssetLibraryConnectedMediaPreviewModal() {
  const store = useStore();
  const { mediaPreview } = store.state;
  const { toggleBookmark, requestMoveToTrash, canDeleteAsset } =
    useAssetLibraryAssetActions();
  const previewAsset = buildDisplayItemFromMediaPreview(mediaPreview);
  const deletableAsset = resolveDeletablePreviewAsset(previewAsset, store.state);
  const moveToTrashDisabled = !canDeleteAsset(deletableAsset);

  return (
    <AssetLibraryMediaPreviewModal
      open={mediaPreview.open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          store.closeMediaPreview();
        }
      }}
      title={mediaPreview.title}
      mediaUrl={mediaPreview.mediaUrl}
      mediaType={mediaPreview.mediaType}
      createdAt={mediaPreview.createdAt}
      createdBy={mediaPreview.createdBy}
      isBookmarked={mediaPreview.isBookmarked}
      showMainLibraryPreviewActions={mediaPreview.showMainLibraryPreviewActions}
      mediaSelection={mediaPreview.mediaSelection}
      showOpenWithActions={mediaPreview.showOpenWithActions}
      onBookmark={
        previewAsset
          ? () => {
              void toggleBookmark(previewAsset);
            }
          : undefined
      }
      onCopyShareLink={
        previewAsset
          ? () => {
              const shareUrl = buildAssetLibraryShareUrl(previewAsset);
              if (shareUrl) {
                void copyAssetLibraryShareLink(shareUrl);
              }
            }
          : undefined
      }
      onMoveToTrash={
        previewAsset ? () => requestMoveToTrash(previewAsset) : undefined
      }
      moveToTrashDisabled={moveToTrashDisabled}
    />
  );
}
